import { createContext } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';

/**
 * The slice of the location the subscription-free read hands out.
 *
 * Narrower than react-router's `Location` on purpose. `readLocation()` is a plain function call, so
 * neither rules-of-hooks nor exhaustive-deps can see it, and nothing stops someone writing
 * `const { pathname } = readLocation()` at the top of a component body: it compiles, it lints, it
 * passes every test that does not navigate, and in production it freezes at mount and stamps
 * `returnTo` with a page the user already left. Dropping `state` and `key` does not stop that call,
 * but it removes the two fields whose whole meaning is "as of this history entry" - the ones a
 * render-phase read would corrupt most quietly - and it keeps the type honest about what this seam
 * is for, which is composing a `returnTo`. `hash` is left out for the same reason: no caller needs
 * it, and it is cheap to add back deliberately.
 *
 * This is a type-level narrowing, not a runtime copy - the provider still returns the live location
 * object, so a deliberate cast could still reach `state`. Widen this type instead if you need more.
 */
export type StableLocationRead = Pick<Location, 'pathname' | 'search'>;

/**
 * Subscription-free access to the router, for components that need to NAVIGATE or to READ the
 * current URL but must not re-render when it changes.
 *
 * Why this exists at all: in react-router 6.30.4 both `useNavigate()` and `useLocation()` make the
 * calling component a context consumer, and React re-renders context consumers regardless of
 * `memo()` or byte-identical props. `useLocation()` reads `LocationContext`; `useNavigate()` reads
 * `RouteContext` (for `isDataRoute`, react-router.development.js `useNavigate`), and under
 * `createBrowserRouter` + `RouterProvider` - which is what `src/index.tsx` mounts - `_renderMatches`
 * builds a fresh `routeContext` object on every router state change, so that context's identity
 * flips on every navigation too. A list leaf that merely wants a tap to go somewhere therefore pays
 * a full re-render of the whole list on any URL change anywhere in the app, including a
 * `replace` that only touches a query param.
 *
 * Measured on the event page's 20-card feed: a `?tab=` flip re-rendered all 20 `memo()`-wrapped
 * cards, with byte-identical props, on the tap that is LEAVING the feed. Routing those cards
 * through this context takes that count to zero.
 *
 * It is not the only way to broadcast that churn. A provider whose context VALUE is rebuilt per
 * render does the same thing to its own consumers, which is why `AuthProvider`'s `openDraftDialog`
 * and its cross-tab handlers are `useCallback`s and not inline arrows. AuthProvider itself now
 * navigates through this seam too, so a URL change no longer re-renders it at all; those callbacks
 * remain as the guard for every OTHER thing that re-renders it.
 *
 * Both members are ref-backed and the context value is created once, so consuming them costs
 * nothing at render time and never invalidates a memo.
 */
export interface StableRouterValue {
  /**
   * `useNavigate()`'s function, reached through a ref at call time. It matches `useNavigate()` for
   * absolute paths, `Partial<Path>` objects, bare `?search` / `#hash` strings and numeric deltas,
   * which is everything `src` uses. It does NOT match it for route-relative values, because it is
   * bound where the provider is mounted rather than at the caller's route, and it drops the return
   * value. `StableRouterProvider` enumerates both and warns on the first in dev.
   */
  navigate: NavigateFunction;
  /**
   * The location as of the moment this is CALLED, not the render that produced the caller. That
   * distinction is the whole point for `returnTo` stamps: a component that no longer subscribes
   * cannot re-render on a URL change, so a value captured during render would be frozen at mount
   * and would send the user back to a URL they have since left.
   */
  readLocation: () => StableLocationRead;
}

// Default `undefined`, matching the AuthContext / LocaleContext / EventStreamContext convention:
// the consumer hooks throw when used outside `StableRouterProvider` so missing provider wiring
// fails loudly instead of silently degrading (a silent fallback to the react-router hooks would
// reinstate exactly the subscription this exists to remove, and nothing would ever notice).
export const StableRouterContext = createContext<StableRouterValue | undefined>(undefined);
